import { LandscapeLayout } from "@/components/layouts";
import { matchmakingService } from "@/game/services/MatchmakingService";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface LocationState {
  opponentType: "human" | "cpu";
  gameMode?: string;
}

export function MatchmakingScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"connecting" | "waiting" | "matched" | "error">(
    "connecting"
  );
  const [message, setMessage] = useState("Initializing matchmaking...");

  // Get opponent type and game mode from route state
  const { opponentType, gameMode = "standard" } = (location.state as LocationState) || {
    opponentType: "human",
    gameMode: "standard"
  };

  useEffect(() => {
    let isCleanedUp = false;

    const startMatchmaking = async () => {
      try {
        setStatus("connecting");
        setMessage("Connecting to matchmaking service...");

        await matchmakingService.findMatch(
          opponentType,
          {
            waiting: (data) => {
              if (!isCleanedUp) {
                setStatus("waiting");
                setMessage(data.message);
              }
            },
            matchFound: (data) => {
              if (!isCleanedUp) {
                setStatus("matched");
                setMessage("Joining game...");
                matchmakingService.disconnect(); // Disconnect from matchmaking service

                // Navigate to game after brief delay
                setTimeout(() => {
                  if (!isCleanedUp) {
                    navigate(`/game/${data.gameId}`);
                  }
                }, 1000);
              }
            },
            error: (data) => {
              if (!isCleanedUp) {
                setStatus("error");
                setMessage(data.message || "Failed to connect to matchmaking");
                matchmakingService.disconnect(); // Disconnect from matchmaking service
              }
            }
          },
          gameMode
        );
      } catch (error) {
        console.error("Matchmaking error:", error);
        if (!isCleanedUp) {
          setStatus("error");
          setMessage(error instanceof Error ? error.message : "Failed to connect to matchmaking");
        }
      }
    };

    startMatchmaking();

    // Cleanup function
    return () => {
      isCleanedUp = true;
      matchmakingService.disconnect();
    };
  }, [opponentType, navigate]);

  const handleCancel = () => {
    navigate("/");
  };

  const getStatusColor = () => {
    switch (status) {
      case "connecting":
        return "text-blue-400";
      case "waiting":
        return "text-amber-400";
      case "matched":
        return "text-green-400";
      case "error":
        return "text-red-400";
      default:
        return "text-gray-400";
    }
  };

  const getSpinnerClass = () => {
    if (status === "error" || status === "matched") return "hidden";
    return "animate-spin";
  };

  return (
    <LandscapeLayout>
      {/* Main Content - Centered Split Layout */}
      <div className="flex-1 flex items-center justify-center px-8 py-6">
        <div className="flex items-center gap-16">
          {/* Spinner */}
          <div className="flex items-center justify-center">
            <div className="relative">
              <svg
                className={`w-24 h-24 ${getSpinnerClass()} ${getStatusColor()}`}
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>

              {status === "error" && (
                <svg
                  className="w-24 h-24 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              )}

              {status === "matched" && (
                <svg
                  className="w-24 h-24 text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
          </div>

          {/* Status Text and Action */}
          <div className="flex flex-col items-start justify-center">
            <h2 className={`text-3xl font-bold mb-4 ${getStatusColor()}`}>
              {status === "connecting" && "Connecting..."}
              {status === "waiting" && "Searching..."}
              {status === "matched" && "Match Found!"}
              {status === "error" && "Connection Error"}
            </h2>

            <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-lg">{message}</p>

            {/* Cancel Button */}
            <button
              onClick={handleCancel}
              className="bg-gradient-to-r from-gray-600 to-gray-700 text-white text-base font-semibold py-3 px-8 rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95 hover:from-gray-500 hover:to-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full text-center py-2 text-xs text-indigo-300">
        <p>© {new Date().getFullYear()} MoonGammon • v2.0.0</p>
      </div>
    </LandscapeLayout>
  );
}

export default MatchmakingScreen;
